<script setup>
// Одне повідомлення власного чату ефіру.
//
// Колір ніка рахується з самого імені (див. chatNickColor) — тобто в усіх
// глядачів той самий автор однакового кольору, і роздавати кольори з
// сервера не треба. Виняток — той, хто вибрав колір сам у налаштуваннях
// чату: такий колір приїжджає в самому повідомленні, разом зі значком.
//
// Медіа — до пʼяти картинок або одне відео, за бажанням автора під
// спойлером: у прямому ефірі кинути в чат можна що завгодно, і глядач має
// вирішувати сам, чи дивитись.

const props = defineProps({
  message: { type: Object, required: true },
  canModerate: { type: Boolean, default: false },
  // Свої повідомлення підсвічуємо — у швидкій стрічці інакше не знайти,
  // що ти щойно написав
  meId: { type: Number, default: null },
  // Своє імʼя: за ним впізнаємо звернення «@нік» до себе
  meName: { type: String, default: '' },
  // Чи є кому відповідати: у незалогіненого й у джерела OBS кнопки
  // відповіді немає — писати вони однаково не можуть
  canReply: { type: Boolean, default: false },
  // Сюди щойно перейшли по цитаті — підсвічуємо на кілька секунд
  flash: { type: Boolean, default: false }
})

const emit = defineEmits(['delete', 'mute', 'reply', 'react', 'mention', 'jump'])

const media = useStrapiMedia
const { emotes: emoteMap, load: loadEmotes } = useEmotes()
const menuOpen = ref(false)

// Вкладення — до пʼяти картинок або відео (див. ChatMediaGrid). Старі
// повідомлення несуть одне медіа обʼєктом, нові — масивом
const hasMedia = computed(() => {
  const list = props.message.media
  return Array.isArray(list) ? list.length > 0 : !!list
})

// --- Реакції, як у Telegram ---
const pickerOpen = ref(false)
const reactBtn = ref(null)

const reactions = computed(() =>
  props.message.deleted || props.message.pending
    ? []
    : Object.entries(props.message.reactions || {})
        .filter(([, who]) => Array.isArray(who) && who.length)
        .map(([key, who]) => ({
          key,
          count: who.length,
          mine: !!props.meId && who.includes(props.meId),
          emote: key.startsWith(':') ? key.slice(1) : null
        }))
)

// Картинки смайлів-реакцій потрібні, лише якщо вони є в повідомленні
watch(
  () => reactions.value.some((r) => r.emote),
  (has) => {
    if (has) loadEmotes()
  },
  { immediate: true }
)

const emoteSrc = (name) => emoteMap.value?.[name] || ''

const toggleReaction = (key) => {
  if (!props.canReply) return
  emit('react', props.message.id, key)
}

const pickReaction = (key) => {
  pickerOpen.value = false
  toggleReaction(key)
}

const isMine = computed(() => props.meId && props.message.userId === props.meId)

// Свій колір, якщо глядач його поставив (див. ChatLiveSettings); інакше —
// той, що рахується з імені, тобто в усіх однаковий
const color = computed(() => props.message.color || nickColor(props.message.author))

const badge = computed(() => (props.message.badge ? media(props.message.badge) : ''))

// --- Значок великим планом ---
//
// У рядку чату значок завбільшки з літеру — розгледіти на ньому щось
// дрібніше за силует неможливо. Тому на наведення показуємо його великим.
//
// Малюємо через Teleport у body, а не поруч зі значком: стрічка чату
// прокручується (overflow-y), і будь-що абсолютно позиційоване всередині неї
// обрізалось би її ж межами — рівно на верхньому повідомленні, де підказка й
// потрібна найчастіше.
//
// Плаваюче вікно чату при цьому висить на z-index 2147483000 (див.
// live/Chat.vue) — тобто підказка з «просто великим» z-index малювалась би
// під ним, і в режимі вікна її не було б видно взагалі. Тому беремо на
// одиницю більше за нього.
//
// Координати рахуємо на момент наведення, position: fixed. Тож і ховати
// підказку треба на прокрутку — інакше вона повисне посеред екрана
// Розміри самої підказки: картинка 96 + рамка з підписом. Потрібні, щоб
// вирішити, вистачає місця над значком чи показувати під ним, і щоб не
// вилізти за край вікна
const BADGE_PREVIEW_W = 112
const BADGE_PREVIEW_H = 136
const BADGE_GAP = 8

const badgeRef = ref(null)
const badgeOpen = ref(false)
const badgeBox = ref({ left: 0, top: 0, above: true })

const openBadge = () => {
  const el = badgeRef.value
  if (!el || !badge.value) return

  const box = el.getBoundingClientRect()
  // Над значком, якщо туди влазить; інакше під ним — у першому рядку чату
  // місця вгорі немає
  const above = box.top > BADGE_PREVIEW_H + BADGE_GAP

  badgeBox.value = {
    // Тримаємо підказку в межах вікна: значок буває майже біля краю вузької
    // колонки чату
    left: Math.min(
      Math.max(box.left + box.width / 2, BADGE_PREVIEW_W / 2 + 8),
      window.innerWidth - BADGE_PREVIEW_W / 2 - 8
    ),
    top: above ? box.top - BADGE_GAP : box.bottom + BADGE_GAP,
    above
  }

  badgeOpen.value = true
}

const closeBadge = () => {
  badgeOpen.value = false
}

// Прокрутка будь-де (сама стрічка, сторінка під нею) — підказка більше не
// стоїть там, де стояв значок. capture: true, бо крутять саме внутрішній
// контейнер, а не вікно
watch(badgeOpen, (open) => {
  if (!import.meta.client) return
  if (open) {
    window.addEventListener('scroll', closeBadge, true)
    window.addEventListener('resize', closeBadge)
  } else {
    window.removeEventListener('scroll', closeBadge, true)
    window.removeEventListener('resize', closeBadge)
  }
})

onBeforeUnmount(() => {
  if (!import.meta.client) return
  window.removeEventListener('scroll', closeBadge, true)
  window.removeEventListener('resize', closeBadge)
})

// Звернення до себе підсвічуємо цілим рядком, а не тільки самим ніком:
// у стрічці, що біжить, кольорове слово посеред тексту легко проґавити
const mentionsMe = computed(() => {
  const name = props.meName
  if (!name || !props.message.text || props.message.deleted) return false
  return props.message.text
    .split(/\s+/)
    .some((token) => token.toLowerCase().replace(/[^a-z0-9_.@-]+$/i, '') === `@${name.toLowerCase()}`)
})

// Цитата над відповіддю: на що саме відповіли
const quote = computed(() => props.message.replyTo || null)

const quoteText = computed(() => {
  const parent = quote.value
  if (!parent) return ''
  if (parent.deleted) return 'повідомлення видалено'
  return parent.text || (parent.media ? 'медіа' : '')
})

const time = computed(() => {
  const date = new Date(props.message.at)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })
})

// Скільки мовчати. Довші строки — вже робота адмінки, туди й ходити
const MUTES = [
  { label: '5 хв', seconds: 300 },
  { label: '30 хв', seconds: 1800 },
  { label: '1 год', seconds: 3600 },
  { label: '1 день', seconds: 86400 }
]

const mute = (seconds) => {
  menuOpen.value = false
  emit('mute', { userId: props.message.userId, seconds })
}
</script>

<template>
  <!-- Системний рядок: «такого-то замучено» і подібне -->
  <div
    v-if="message.system"
    class="py-0.5 text-[11px] text-base-content/45 text-center"
  >
    {{ message.text }}
  </div>

  <div
    v-else
    :id="message.pending ? null : `chat-msg-${message.id}`"
    class="group relative px-1 py-0.5 rounded text-sm leading-snug break-words"
    :class="[
      mentionsMe
        ? 'bg-warning/15 border-warning border-l-2 pl-1.5'
        : isMine
          ? 'bg-primary/10'
          : 'hover:bg-base-content/5',
      // Своє, ще не підтверджене сервером: у стрічці воно вже є, але
      // видаляти й відповідати на нього нема на що — ID йому дає сервер
      message.pending ? 'opacity-55' : '',
      flash ? 'ring-1 ring-primary/70' : ''
    ]"
  >
    <!--
      Цитата: на що саме відповіли. Клік веде до самого повідомлення, якщо
      воно ще в стрічці, — інакше на довгій розмові доводилось би гортати
      навмання
    -->
    <button
      v-if="quote"
      type="button"
      class="flex items-center gap-1 mb-0.5 max-w-full text-[11px] text-left text-base-content/45 hover:text-base-content/70"
      :title="`Відповідь: ${quote.author}`"
      @click="emit('jump', quote.id)"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        class="opacity-70 shrink-0"
      >
        <polyline points="9 17 4 12 9 7" />
        <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
      </svg>
      <span
        class="font-semibold shrink-0"
        :style="{ color: quote.color || nickColor(quote.author) }"
        >{{ quote.author }}</span
      >
      <span
        class="truncate"
        :class="quote.deleted ? 'italic' : ''"
        >{{ quoteText }}</span
      >
    </button>

    <span class="mr-1 text-[10px] text-base-content/35 tabular-nums">{{ time }}</span>

    <!--
      Свій значок глядача — як на Twitch, перед іменем. Розміром із рядок
      тексту: більший розсунув би стрічку, а вона тут головна. Розгледіти
      його можна наведенням — тоді значок показується великим.

      Клік робить те саме: на телефоні наведення немає, а значок там такий
      самий дрібний
    -->
    <img
      v-if="badge"
      ref="badgeRef"
      :src="badge"
      class="inline-block align-middle mr-1 w-4 h-4 object-contain cursor-zoom-in"
      alt=""
      loading="lazy"
      decoding="async"
      @mouseenter="openBadge"
      @mouseleave="closeBadge"
      @click.stop="badgeOpen ? closeBadge() : openBadge()"
    />

    <Teleport
      v-if="badgeOpen"
      to="body"
    >
      <div
        class="z-[2147483001] fixed bg-base-200 shadow-xl p-2 border border-base-content/15 rounded-lg pointer-events-none"
        :style="{
          left: `${badgeBox.left}px`,
          top: `${badgeBox.top}px`,
          transform: badgeBox.above ? 'translate(-50%, -100%)' : 'translate(-50%, 0)'
        }"
      >
        <img
          :src="badge"
          class="block w-[96px] h-[96px] object-contain"
          alt=""
        />
        <p class="mt-1 max-w-[96px] text-[10px] text-base-content/50 text-center truncate">
          {{ message.author }}
        </p>
      </div>
    </Teleport>

    <!-- Значок модератора: він же відповідь на «а хто це такий, що
         видаляє повідомлення» -->
    <span
      v-if="message.moderator"
      class="inline-flex align-middle mr-1 text-primary"
      title="Модератор чату"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M12 2 4 5v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V5l-8-3Zm-1 14-4-4 1.4-1.4L11 13.2l4.6-4.6L17 10l-6 6Z" />
      </svg>
    </span>

    <!-- Клік по ніку підставляє «@нік» у поле: у чаті це звичніший спосіб
         звернутись, ніж набирати імʼя руками -->
    <span
      class="font-semibold"
      :class="canReply ? 'cursor-pointer hover:underline' : ''"
      :style="{ color }"
      :title="canReply ? 'Згадати в повідомленні' : ''"
      @click="canReply && emit('mention', message.author)"
      >{{ message.author }}</span
    ><span class="mr-1 text-base-content/50">:</span>

    <template v-if="message.deleted">
      <span class="text-[12px] text-base-content/40 italic">
        повідомлення видалено{{ message.deletedBy ? ` · ${message.deletedBy}` : '' }}
      </span>
    </template>

    <template v-else>
      <!--
        Текст пропускаємо через ChatMessageText: він і емоції підставить, і
        посилання на картинку покаже картинкою. Коли до повідомлення вже
        прикріплене наше медіа, картинок із тексту не робимо — одне медіа
        на повідомлення значить одне
      -->
      <ChatMessageText
        v-if="message.text"
        inline
        :text="message.text"
        :max="hasMedia ? 0 : 1"
        :me="meName"
      />

      <ChatMediaGrid
        v-if="hasMedia"
        :media="message.media"
        :spoiler="message.spoiler"
        compact
      />
    </template>

    <!-- Реакції: чіпи з лічильниками під повідомленням -->
    <div
      v-if="reactions.length"
      class="flex flex-wrap gap-1 mt-0.5"
    >
      <button
        v-for="r in reactions"
        :key="r.key"
        type="button"
        class="inline-flex items-center gap-1 px-1.5 border rounded-full h-5 text-[11px] transition-colors"
        :class="[
          r.mine
            ? 'bg-primary/20 border-primary/60'
            : 'bg-base-200/80 border-base-content/10 hover:border-base-content/30',
          canReply ? 'cursor-pointer' : 'cursor-default'
        ]"
        :title="r.mine ? 'Ваша реакція — натисніть, щоб зняти' : 'Додати таку саму'"
        @click="toggleReaction(r.key)"
      >
        <img
          v-if="r.emote && emoteSrc(r.emote)"
          :src="emoteSrc(r.emote)"
          :alt="r.emote"
          class="w-4 h-4 object-contain"
        />
        <span
          v-else-if="r.emote"
          class="max-w-16 text-[10px] truncate"
          >{{ r.emote }}</span
        >
        <span
          v-else
          class="text-sm leading-none"
          >{{ r.key }}</span
        >
        <span
          class="tabular-nums"
          :class="r.mine ? 'text-primary font-semibold' : 'opacity-70'"
          >{{ r.count }}</span
        >
      </button>
    </div>

    <!--
      Модерація: зʼявляється на наведення, щоб не миготіти в стрічці.
      Кошик є і в модератора (на будь-якому повідомленні), і в автора на
      власному — прибрати за собою не та дія, заради якої кличуть модератора
    -->
    <div
      v-if="(canReply || canModerate || isMine) && !message.deleted && !message.pending"
      class="top-0 right-0 absolute flex items-center gap-0.5 bg-base-200/95 opacity-0 group-hover:opacity-100 focus-within:opacity-100 shadow px-1 rounded transition-opacity"
    >
      <button
        v-if="canReply"
        ref="reactBtn"
        class="hover:bg-base-300 p-1 rounded text-base-content/60 hover:text-primary"
        :class="{ 'text-primary': pickerOpen }"
        title="Додати реакцію"
        @click="pickerOpen = !pickerOpen"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
          />
          <path d="M8 14s1.5 2 4 2 4-2 4-2" />
          <line
            x1="9"
            y1="9"
            x2="9.01"
            y2="9"
          />
          <line
            x1="15"
            y1="9"
            x2="15.01"
            y2="9"
          />
        </svg>
      </button>

      <button
        v-if="canReply"
        class="hover:bg-base-300 p-1 rounded text-base-content/60 hover:text-primary"
        title="Відповісти"
        @click="emit('reply', message)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="9 17 4 12 9 7" />
          <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
        </svg>
      </button>

      <button
        v-if="canModerate && message.userId && !isMine"
        class="hover:bg-base-300 p-1 rounded text-base-content/60 hover:text-warning"
        title="Заборонити писати"
        @click="menuOpen = !menuOpen"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle
            cx="12"
            cy="12"
            r="9"
          />
          <line
            x1="5.6"
            y1="5.6"
            x2="18.4"
            y2="18.4"
          />
        </svg>
      </button>

      <!-- Кошик — тільки тому, кому справді є що видаляти: своє або, у
           модератора, будь-чиє. Сама панель тепер відкривається й заради
           кнопки відповіді, тож без цієї умови кошик світився б усім -->
      <button
        v-if="canModerate || isMine"
        class="hover:bg-base-300 p-1 rounded text-base-content/60 hover:text-error"
        :title="isMine ? 'Видалити своє повідомлення' : 'Видалити повідомлення'"
        @click="emit('delete', message.id)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      </button>
    </div>

    <div
      v-if="menuOpen"
      class="top-6 right-0 z-40 absolute flex flex-col bg-base-200 shadow-xl border border-base-content/10 rounded overflow-hidden"
    >
      <button
        v-for="option in MUTES"
        :key="option.seconds"
        class="hover:bg-base-300 px-3 py-1 text-[11px] text-left whitespace-nowrap"
        @click="mute(option.seconds)"
      >
        Мовчанка {{ option.label }}
      </button>
      <button
        class="hover:bg-base-300 px-3 py-1 border-base-content/10 border-t text-[11px] text-left whitespace-nowrap"
        @click="mute(0)"
      >
        Зняти мовчанку
      </button>
    </div>
  
    <ChatReactionPicker
      v-if="pickerOpen"
      :anchor="reactBtn"
      @pick="pickReaction"
      @close="pickerOpen = false"
    />
  </div>
</template>
